import type BaseObject from "../models/BaseObject";
import type ConnectableObject from "../models/ConnectableObject";
import type { ConnectableEndpoint, ConnectionEndpointSurface } from "./ConnectionEndpointProvider";
import { visibleControlPoint } from "./controlAnchorPoint";
import { controlExitDirection } from "./controlExitDirection";
import { measureEndpointContour, navigatorEndpointGeometry } from "./endpointContour";

/** Exposes only real visible devices; hidden controls and container fallback anchors cannot create connections. */
export function connectableControlEndpoint(
  object: BaseObject | undefined,
  element: Element | null,
  surface: ConnectionEndpointSurface,
): ConnectableEndpoint | undefined {
  if (!object || object.isDeleted || !element) return undefined;
  if (!["Consumer", "Button", "ButtonChannel", "Channel", "Actor", "Scene", "Multiplexer"].includes(object.className)) return undefined;
  const device = object as ConnectableObject;
  if (["Ignored", "Disabled"].includes(device.channelType)) return undefined;
  const point = visibleControlPoint(element);
  if (!point) return undefined;
  const exitDirection = surface === "navigator" ? { x: 1, y: 0 }
    : surface === "button-editor" ? undefined : controlExitDirection(element);
  const contour = measureEndpointContour(element, point, surface);
  // Candidate hit testing stays on the actual row; grabs measure the open flyout separately.
  const geometry = surface === "navigator" ? navigatorEndpointGeometry(element, point, false) : { point, contour };
  const hoverElement = element.closest("[data-connection-control-region]") ?? element;
  return { key: object.id, object: device, surface, ...geometry, exitDirection, element, hoverElement };
}

/** Enumerates concrete child controls without looking up existing connections. */
export function collectConnectableControls(
  root: Element | null,
  surface: ConnectionEndpointSurface,
  resolveObject: (id: string) => BaseObject | undefined,
): ConnectableEndpoint[] {
  const endpoints: ConnectableEndpoint[] = [];
  const occurrences = new Map<string, number>();
  for (const element of Array.from(root?.querySelectorAll('[data-connection-object-id]') ?? [])) {
    const id = element.getAttribute("data-connection-object-id");
    if (!id) continue;
    const occurrence = occurrences.get(id) ?? 0;
    occurrences.set(id, occurrence + 1);
    const endpoint = connectableControlEndpoint(resolveObject(id), element, surface);
    if (!endpoint) continue;
    // A shared channel can have multiple physical keys; each must remain reachable.
    endpoint.key = `${id}:${occurrence}`;
    endpoints.push(endpoint);
  }
  return endpoints;
}
